/**
 * useDeviceKeys.js — React hook for encrypted key management
 *
 * Handles:
 *  1. Handshake with Lambda to get encrypted K2
 *  2. Decrypt K2 using locally-generated K1
 *  3. Store K2 in memory (per device, session-only)
 *  4. Expose getK2() for other components (e.g. DeviceStatus)
 */
import { useRef, useCallback, useState } from 'react';
import { generateK1, aesDecrypt } from './cryptoUtils';

const API_BASE = ''; // relative — handshake hits the proxy on the same origin (was a dead ngrok URL)

const HANDSHAKE_TIMEOUT_MS = 12000;

export function useDeviceKeys() {
  const keysRef = useRef({});
  const [pendingKeys, setPendingKeys] = useState(new Set());
  const [failedKeys,  setFailedKeys]  = useState(new Set());

  const handshake = useCallback(async (macId, macIdUppercase) => {
    const mac = (macIdUppercase || macId).toUpperCase();
    const macLower = macId.toLowerCase();

    try {
      setPendingKeys(prev => new Set(prev).add(macLower));
      console.log(`[HANDSHAKE] Starting for ${mac}`);

      // 1. Generate K1 locally (MUST await — async WebCrypto)
      const k1 = await generateK1(mac);
      console.log(`[HANDSHAKE] K1: ${k1.substring(0, 16)}…`);

      // 2. Call server handshake endpoint
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HANDSHAKE_TIMEOUT_MS);

      const res = await fetch(`${API_BASE}/api/handshake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          macId: mac,
          payload: { IM: mac, KY: '', CMD: 'HDS' },
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (!res.ok) throw new Error(`Handshake API ${res.status}`);
      const data = await res.json();

      if (!data.success || !data.KY) {
        throw new Error(data.error || 'No KY in handshake response');
      }

      console.log(`[HANDSHAKE] Encrypted K2 received (${data.KY.length} hex chars)`);

      // 3. Decrypt K2 with K1 (MUST await — async WebCrypto)
      const k2Hex = await aesDecrypt(data.KY, k1);
      console.log(`[HANDSHAKE] K2 decrypted: ${k2Hex.substring(0, 16)}…`);

      // 4. Store in memory
      keysRef.current[macLower] = {
        k2: k2Hex,
        k1,
        version: data.version || 0,
        retrievedAt: Date.now(),
      };
      // Mirror into global cache so non-hook callers (TAM ACK, App tamper
      // listener) can reuse the same K2 without re-handshaking.
      window.__hoagsK2 = window.__hoagsK2 || {};
      window.__hoagsK2[macLower] = k2Hex;

      setPendingKeys(prev => { const s = new Set(prev); s.delete(macLower); return s; });
      setFailedKeys(prev  => { const s = new Set(prev); s.delete(macLower); return s; });

      console.log(`[HANDSHAKE] ✅ Key stored for ${mac} (v${data.version})`);
      return true;

    } catch (err) {
      console.error(`[HANDSHAKE] ❌ Failed for ${mac}:`, err.message);
      setPendingKeys(prev => { const s = new Set(prev); s.delete(macLower); return s; });
      setFailedKeys(prev  => new Set(prev).add(macLower));
      return false;
    }
  }, []);

  const handshakeMultiple = useCallback(async (devices) => {
    if (!devices || devices.length === 0) return 0;
    console.log(`[HANDSHAKE] Bulk: ${devices.length} device(s)`);
    const results = await Promise.allSettled(
      devices.map(d => handshake(d.macid, d.macid)),
    );
    const succeeded = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
    console.log(`[HANDSHAKE] Bulk done: ${succeeded}/${devices.length} succeeded`);
    return succeeded;
  }, [handshake]);

  const getK2 = useCallback((macId) => {
    const entry = keysRef.current[macId.toLowerCase()];
    return entry?.k2 || null;
  }, []);

  const hasKey = useCallback((macId) => {
    return !!keysRef.current[macId.toLowerCase()]?.k2;
  }, []);

  const clearKeys = useCallback(() => {
    keysRef.current = {};
    setPendingKeys(new Set());
    setFailedKeys(new Set());
  }, []);

  const getStats = useCallback(() => {
    return {
      total:   Object.keys(keysRef.current).length,
      pending: pendingKeys.size,
      failed:  failedKeys.size,
    };
  }, [pendingKeys, failedKeys]);

  return {
    handshake,
    handshakeMultiple,
    getK2,
    hasKey,
    clearKeys,
    getStats,
    pendingKeys,
    failedKeys,
  };
}


// /**
//  * useDeviceKeys.js — React hook for encrypted key management
//  *
//  * DEBUG MODE:
//  * ----------------------------------------------------------------
//  * TEMPORARY FIX FOR K2 MISMATCH
//  *
//  * Firmware currently uses HARD-CODED K2:
//  *   603DEB1015CA71BE2B73AEF0857D77811F352C073B6108D72D9810A30914DFF4
//  *
//  * So instead of decrypting KY from handshake,
//  * we temporarily force the SAME K2 in frontend.
//  *
//  * This is ONLY for debugging to verify:
//  *   DS request decrypts correctly on device
//  *   device responds on /response topic
//  *
//  * REMOVE after confirmation.
//  * ----------------------------------------------------------------
//  */

// import { useRef, useCallback, useState } from 'react';
// import { generateK1 } from './cryptoUtils';

// const API_BASE = process.env.NODE_ENV === 'production'
//   ? 'https://preroyal-maira-straightforwardly.ngrok-free.dev'
//   : 'http://localhost:4001';

// const HANDSHAKE_TIMEOUT_MS = 12000;

// /**
//  * ── DEBUG HARD-CODED K2 ───────────────────────────────────────
//  * MUST MATCH firmware cloudConnect.c hardcoded K2
//  */
// const DEBUG_FORCE_K2 =
//   '603DEB1015CA71BE2B73AEF0857D77811F352C073B6108D72D9810A30914DFF4';

// /**
//  * Enable / disable debug forced K2
//  */
// const DEBUG_USE_FORCED_K2 = true;

// export function useDeviceKeys() {
//   const keysRef = useRef({});

//   const [pendingKeys, setPendingKeys] = useState(new Set());
//   const [failedKeys, setFailedKeys] = useState(new Set());

//   const handshake = useCallback(async (macId, macIdUppercase) => {
//     const mac = (macIdUppercase || macId).toUpperCase();
//     const macLower = mac.toLowerCase();

//     try {
//       setPendingKeys(prev => new Set(prev).add(macLower));

//       console.log(`[HANDSHAKE] Starting for ${mac}`);

//       /**
//        * Generate K1 (still useful for future real flow)
//        */
//       const k1 = await generateK1(mac);

//       console.log(`[HANDSHAKE] K1 generated: ${k1.substring(0, 16)}…`);

//       /**
//        * ─────────────────────────────────────────────────────────
//        * DEBUG MODE
//        * ─────────────────────────────────────────────────────────
//        */
//       if (DEBUG_USE_FORCED_K2) {
//         console.log('[HANDSHAKE] ⚠️ DEBUG MODE ENABLED');
//         console.log('[HANDSHAKE] Using hardcoded firmware K2');

//         keysRef.current[macLower] = {
//           k2: DEBUG_FORCE_K2,
//           k1,
//           version: 999,
//           retrievedAt: Date.now(),
//           debug: true,
//         };

//         setPendingKeys(prev => {
//           const s = new Set(prev);
//           s.delete(macLower);
//           return s;
//         });

//         setFailedKeys(prev => {
//           const s = new Set(prev);
//           s.delete(macLower);
//           return s;
//         });

//         console.log(
//           `[HANDSHAKE] ✅ DEBUG K2 stored for ${mac}`
//         );

//         console.log(
//           `[HANDSHAKE] DEBUG K2: ${DEBUG_FORCE_K2.substring(0, 32)}…`
//         );

//         return true;
//       }

//       /**
//        * ─────────────────────────────────────────────────────────
//        * REAL HANDSHAKE FLOW
//        * ─────────────────────────────────────────────────────────
//        */

//       const controller = new AbortController();

//       const timer = setTimeout(
//         () => controller.abort(),
//         HANDSHAKE_TIMEOUT_MS
//       );

//       const res = await fetch(`${API_BASE}/api/handshake`, {
//         method: 'POST',

//         headers: {
//           'Content-Type': 'application/json',
//           'ngrok-skip-browser-warning': 'true',
//         },

//         body: JSON.stringify({
//           macId: mac,

//           payload: {
//             IM: mac,
//             KY: '',
//             CMD: 'HDS',
//           },
//         }),

//         signal: controller.signal,
//       });

//       clearTimeout(timer);

//       if (!res.ok) {
//         throw new Error(`Handshake API ${res.status}`);
//       }

//       const data = await res.json();

//       if (!data.success || !data.KY) {
//         throw new Error(
//           data.error || 'No KY in handshake response'
//         );
//       }

//       console.log(
//         `[HANDSHAKE] Encrypted K2 received (${data.KY.length} hex chars)`
//       );

//       /**
//        * REAL decrypt flow disabled for debug
//        */
//       throw new Error(
//         'REAL decrypt flow disabled while DEBUG_USE_FORCED_K2=true'
//       );

//     } catch (err) {

//       console.error(
//         `[HANDSHAKE] ❌ Failed for ${mac}:`,
//         err.message
//       );

//       setPendingKeys(prev => {
//         const s = new Set(prev);
//         s.delete(macLower);
//         return s;
//       });

//       setFailedKeys(prev =>
//         new Set(prev).add(macLower)
//       );

//       return false;
//     }

//   }, []);

//   const handshakeMultiple = useCallback(async (devices) => {

//     if (!devices || devices.length === 0) {
//       return 0;
//     }

//     console.log(
//       `[HANDSHAKE] Bulk: ${devices.length} device(s)`
//     );

//     const results = await Promise.allSettled(
//       devices.map(d => handshake(d.macid, d.macid))
//     );

//     const succeeded =
//       results.filter(
//         r =>
//           r.status === 'fulfilled' &&
//           r.value === true
//       ).length;

//     console.log(
//       `[HANDSHAKE] Bulk done: ${succeeded}/${devices.length} succeeded`
//     );

//     return succeeded;

//   }, [handshake]);

//   const getK2 = useCallback((macId) => {

//     const entry =
//       keysRef.current[macId.toLowerCase()];

//     return entry?.k2 || null;

//   }, []);

//   const hasKey = useCallback((macId) => {

//     return !!keysRef.current[
//       macId.toLowerCase()
//     ]?.k2;

//   }, []);

//   const clearKeys = useCallback(() => {

//     keysRef.current = {};

//     setPendingKeys(new Set());

//     setFailedKeys(new Set());

//   }, []);

//   const getStats = useCallback(() => {

//     return {
//       total: Object.keys(keysRef.current).length,
//       pending: pendingKeys.size,
//       failed: failedKeys.size,
//     };

//   }, [pendingKeys, failedKeys]);

//   return {
//     handshake,
//     handshakeMultiple,
//     getK2,
//     hasKey,
//     clearKeys,
//     getStats,
//     pendingKeys,
//     failedKeys,
//   };
// }